import React, { useState } from 'react';
import { Upload, X, Loader } from 'lucide-react';
import { apiClient } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function ImageUploader({ images = [], onChange, maxImages = 10 }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0]; // Upload one at a time
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, WEBP ou GIF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }

    if (images.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images atteint`);
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await apiClient.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Add full URL (backend URL + relative path)
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange([...images, imageUrl]);
      toast.success('Image uploadée avec succès !');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  };

  // Drag & Drop reordering handlers
  const handleImageDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleImageDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const [draggedItem] = newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedItem);
    
    onChange(newImages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleImageDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="space-y-3">
      {/* Upload area */}
      <div
        onDrop={handleDrop}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        className={`relative border-2 border-dashed rounded-xl p-6 transition-all ${
          dragActive 
            ? 'border-[#00aaff] bg-[#00aaff]/5' 
            : 'border-gray-200 hover:border-[#00aaff]/50'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handleFileUpload(e.target.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading || images.length >= maxImages}
        />
        
        <div className="text-center">
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader size={32} className="text-[#00aaff] animate-spin" />
              <p className="text-sm text-gray-500">Upload en cours...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-[#00aaff]/10 flex items-center justify-center">
                <Upload size={24} className="text-[#00aaff]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Cliquez ou glissez une image
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  JPG, PNG, WEBP ou GIF (max 5MB)
                </p>
              </div>
              {images.length > 0 && (
                <p className="text-xs text-[#00aaff] font-medium">
                  {images.length}/{maxImages} images
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {images.map((url, index) => (
            <div 
              key={index} 
              draggable
              onDragStart={(e) => handleImageDragStart(e, index)}
              onDragOver={(e) => handleImageDragOver(e, index)}
              onDrop={(e) => handleImageDrop(e, index)}
              onDragEnd={handleImageDragEnd}
              className={`relative group aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-move ${
                draggedIndex === index 
                  ? 'opacity-50 border-[#00aaff] scale-95' 
                  : dragOverIndex === index
                  ? 'border-[#00aaff] border-dashed scale-105'
                  : 'border-gray-200'
              }`}
            >
              <img 
                src={url} 
                alt={`Preview ${index + 1}`}
                className="w-full h-full object-cover pointer-events-none"
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600 z-10"
              >
                <X size={14} />
              </button>
              {index === 0 && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 pointer-events-none">
                  <p className="text-white text-[10px] font-bold uppercase tracking-wider text-center">
                    Image principale
                  </p>
                </div>
              )}
              {/* Drag indicator */}
              <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-white/90 rounded px-2 py-0.5">
                  <p className="text-[10px] font-bold text-gray-600">⋮⋮</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
